import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search, Save, Eye, RotateCcw, Send, Loader2,
  FileText, Share2, Building2, Map, Link as LinkIcon, Bot,
  Plus, Trash2, X, ImageIcon, AlertCircle, CheckCircle2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { websiteAPI } from '../../services/api';
import useDraftPublish from '../../hooks/useDraftPublish';
import MediaPicker from '../../components/media/MediaPicker';

// =====================================================================
// Constants & helpers
// =====================================================================

const SECTION_KEYS = ['seoDefaults', 'schemaOrg', 'redirects', 'robotsExtra'];

const CHANGEFREQ_OPTIONS = [
  { value: 'always', label: 'always' },
  { value: 'hourly', label: 'hourly' },
  { value: 'daily', label: 'daily' },
  { value: 'weekly', label: 'weekly' },
  { value: 'monthly', label: 'monthly' },
  { value: 'yearly', label: 'yearly' },
  { value: 'never', label: 'never' },
];

const ORG_TYPES = [
  { value: 'MedicalBusiness', label: 'MedicalBusiness' },
  { value: 'MedicalOrganization', label: 'MedicalOrganization' },
  { value: 'LocalBusiness', label: 'LocalBusiness' },
];

const WEEKDAYS = [
  { value: 'Monday', labelAr: 'الإثنين' },
  { value: 'Tuesday', labelAr: 'الثلاثاء' },
  { value: 'Wednesday', labelAr: 'الأربعاء' },
  { value: 'Thursday', labelAr: 'الخميس' },
  { value: 'Friday', labelAr: 'الجمعة' },
  { value: 'Saturday', labelAr: 'السبت' },
  { value: 'Sunday', labelAr: 'الأحد' },
];

const REDIRECT_CODES = [301, 302, 307, 308];

const emptyDefaults = {
  seoDefaults: {
    titleTemplate: { ar: '%s | {{siteName}}', en: '%s | {{siteName}}' },
    defaultTitle: { ar: '', en: '' },
    defaultDescription: { ar: '', en: '' },
    keywords: [],
    ogImageUrl: '',
    twitterHandle: '',
    sitemapDefaultPriority: 0.7,
    sitemapDefaultChangefreq: 'weekly',
  },
  schemaOrg: {
    organizationType: 'MedicalBusiness',
    foundingDate: '',
    priceRange: '',
    geo: { lat: '', lng: '' },
    openingHoursSpecification: [],
  },
  redirects: [],
  robotsExtra: '',
};

function deepMerge(base, patch) {
  if (Array.isArray(patch)) return patch;
  if (patch && typeof patch === 'object') {
    const out = { ...(base && typeof base === 'object' ? base : {}) };
    for (const k of Object.keys(patch)) {
      out[k] = deepMerge(out[k], patch[k]);
    }
    return out;
  }
  return patch;
}

function pickInitialSection(snapshot, key) {
  const fromSnap = snapshot?.[key];
  const baseline = emptyDefaults[key];
  if (fromSnap === undefined || fromSnap === null) {
    return JSON.parse(JSON.stringify(baseline));
  }
  if (typeof baseline === 'string' || Array.isArray(baseline)) {
    return fromSnap;
  }
  return deepMerge(JSON.parse(JSON.stringify(baseline)), fromSnap);
}

function shallowEqualJSON(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
}

function formatRelativeAr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'قبل لحظات';
  if (diff < 3600) return `قبل ${Math.floor(diff / 60)} دقيقة`;
  if (diff < 86400) return `قبل ${Math.floor(diff / 3600)} ساعة`;
  if (diff < 604800) return `قبل ${Math.floor(diff / 86400)} يوم`;
  return d.toLocaleDateString('ar-SA-u-ca-gregory');
}

function applyTitleTemplate(template, pageTitle, siteName) {
  if (!template) return pageTitle || '';
  return String(template)
    .replace(/%s/g, pageTitle || '')
    .replace(/\{\{siteName\}\}/g, siteName || '');
}

// =====================================================================
// Reusable bits (local copies; no cross-file coupling)
// =====================================================================

const SectionCard = ({ icon: Icon, title, subtitle, dirty, saving, onSave, children, accent = 'blue' }) => {
  const accentMap = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    violet: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-blue-100 text-blue-600',
    slate: 'bg-slate-100 text-slate-600',
    pink: 'bg-blue-100 text-blue-600',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${accentMap[accent] || accentMap.blue}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-800">{title}</h3>
            {subtitle ? <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p> : null}
          </div>
        </div>
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          حفظ هذا القسم
        </button>
      </div>
      <div className="p-5">{children}</div>
    </motion.div>
  );
};

const TextField = ({ label, value, onChange, placeholder, dir, type = 'text', hint, step, min, max }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      dir={dir}
      step={step}
      min={min}
      max={max}
      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
    />
    {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, dir, rows = 3, mono, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      dir={dir}
      rows={rows}
      className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm ${mono ? 'font-mono' : ''}`}
    />
    {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
  </div>
);

// =====================================================================
// Main component
// =====================================================================

const SEOSettings = () => {
  const {
    state, loading, saving, publishing, reverting, previewLoading,
    saveDraft, publish, revert, openPreview,
  } = useDraftPublish({
    getState: websiteAPI.getSiteSettings,
    saveDraft: websiteAPI.saveSiteSettingsDraft,
    publish: websiteAPI.publishSiteSettings,
    revert: websiteAPI.revertSiteSettings,
    previewToken: websiteAPI.siteSettingsPreviewToken,
  });

  const draft = state?.draftSnapshot || null;

  const [sections, setSections] = useState(() => {
    const s = {};
    for (const k of SECTION_KEYS) s[k] = pickInitialSection(null, k);
    return s;
  });

  // Keyword chip staging input (not part of draft, only for UX)
  const [keywordInput, setKeywordInput] = useState('');

  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  useEffect(() => {
    if (!draft) return;
    setSections((prev) => {
      const next = { ...prev };
      for (const k of SECTION_KEYS) {
        next[k] = pickInitialSection(draft, k);
      }
      return next;
    });
  }, [draft]);

  const dirtyMap = useMemo(() => {
    const out = {};
    for (const k of SECTION_KEYS) {
      const serverVal = pickInitialSection(draft, k);
      out[k] = !shallowEqualJSON(sections[k], serverVal);
    }
    return out;
  }, [sections, draft]);

  const anyDirty = SECTION_KEYS.some((k) => dirtyMap[k]);

  const updateSection = useCallback((key, updater) => {
    setSections((prev) => ({
      ...prev,
      [key]: typeof updater === 'function' ? updater(prev[key]) : updater,
    }));
  }, []);

  const saveSection = useCallback(async (key) => {
    if (!dirtyMap[key]) return;
    await saveDraft({ [key]: sections[key] });
  }, [dirtyMap, sections, saveDraft]);

  const saveAllDirty = useCallback(async () => {
    const partial = {};
    let count = 0;
    for (const k of SECTION_KEYS) {
      if (dirtyMap[k]) { partial[k] = sections[k]; count += 1; }
    }
    if (count === 0) return;
    await saveDraft(partial);
  }, [dirtyMap, sections, saveDraft]);

  // Redirect row validation
  const redirectErrors = useMemo(() => {
    const errs = [];
    (sections.redirects || []).forEach((r, idx) => {
      if (r && (r.from || r.to)) {
        if (!r.from || !r.to) {
          errs[idx] = 'الحقلان from و to مطلوبان';
        } else if (r.statusCode && !REDIRECT_CODES.includes(Number(r.statusCode))) {
          errs[idx] = 'الكود يجب أن يكون 301 / 302 / 307 / 308';
        }
      }
    });
    return errs;
  }, [sections.redirects]);

  const hasRedirectErrors = redirectErrors.some(Boolean);

  const onPublish = useCallback(async () => {
    if (hasRedirectErrors) {
      toast.error('توجد تحويلات غير مكتملة — صحّحها قبل النشر');
      return;
    }
    await publish();
  }, [publish, hasRedirectErrors]);

  // --- Keyword handlers ---
  const addKeyword = useCallback(() => {
    const v = (keywordInput || '').trim();
    if (!v) return;
    const current = sections.seoDefaults.keywords || [];
    if (current.includes(v)) { setKeywordInput(''); return; }
    updateSection('seoDefaults', (s) => ({ ...s, keywords: [...current, v] }));
    setKeywordInput('');
  }, [keywordInput, sections.seoDefaults.keywords, updateSection]);

  const removeKeyword = useCallback((kw) => {
    updateSection('seoDefaults', (s) => ({
      ...s,
      keywords: (s.keywords || []).filter((k) => k !== kw),
    }));
  }, [updateSection]);

  // --- Media picker ---
  const onOgImagePick = useCallback((asset) => {
    const url = asset?.publicUrl || asset?.url || '';
    updateSection('seoDefaults', (s) => ({ ...s, ogImageUrl: url }));
    setMediaPickerOpen(false);
  }, [updateSection]);

  // --- Schema.org opening hours ---
  const addOpeningRow = useCallback(() => {
    updateSection('schemaOrg', (s) => ({
      ...s,
      openingHoursSpecification: [
        ...(s.openingHoursSpecification || []),
        { dayOfWeek: [], opens: '08:00', closes: '22:00' },
      ],
    }));
  }, [updateSection]);

  const updateOpeningRow = useCallback((idx, updater) => {
    updateSection('schemaOrg', (s) => {
      const list = [...(s.openingHoursSpecification || [])];
      list[idx] = typeof updater === 'function' ? updater(list[idx]) : updater;
      return { ...s, openingHoursSpecification: list };
    });
  }, [updateSection]);

  const removeOpeningRow = useCallback((idx) => {
    updateSection('schemaOrg', (s) => ({
      ...s,
      openingHoursSpecification: (s.openingHoursSpecification || []).filter((_, i) => i !== idx),
    }));
  }, [updateSection]);

  // --- Redirects ---
  const addRedirect = useCallback(() => {
    updateSection('redirects', (list) => [...(list || []), { from: '', to: '', statusCode: 301 }]);
  }, [updateSection]);

  const updateRedirect = useCallback((idx, next) => {
    updateSection('redirects', (list) => {
      const out = [...(list || [])];
      out[idx] = next;
      return out;
    });
  }, [updateSection]);

  const removeRedirect = useCallback((idx) => {
    updateSection('redirects', (list) => (list || []).filter((_, i) => i !== idx));
  }, [updateSection]);

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" dir="rtl">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  const hasUnpublished = !!state?.hasUnpublishedChanges;
  const isPublished = state?.status === 'published';
  const hasPublishedSnapshot = !!state?.publishedSnapshot;

  const titlePreviewAr = applyTitleTemplate(
    sections.seoDefaults.titleTemplate?.ar,
    'صفحة تجريبية',
    'مختبرك',
  );
  const titlePreviewEn = applyTitleTemplate(
    sections.seoDefaults.titleTemplate?.en,
    'Sample Page',
    'Your Lab',
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 mb-5"
        >
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
            <Search className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إعدادات SEO</h1>
            <p className="text-slate-500 text-sm mt-1">
              تحكّم كامل بكيفية ظهور موقعك في Google ومحركات البحث
            </p>
          </div>
        </motion.div>

        {/* Status bar */}
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#F8FAFC]/80 backdrop-blur border-b border-gray-100 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                hasUnpublished
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : isPublished
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200'
              }`}
            >
              {hasUnpublished ? (
                <>
                  <AlertCircle className="w-3.5 h-3.5" />
                  تغييرات غير منشورة
                </>
              ) : isPublished ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  منشور
                </>
              ) : (
                <>
                  <AlertCircle className="w-3.5 h-3.5" />
                  مسودة فقط
                </>
              )}
            </span>

            <span className="text-xs text-gray-500">
              آخر حفظ: {formatRelativeAr(state?.draftUpdatedAt) || '—'}
            </span>
            {hasPublishedSnapshot && (
              <span className="text-xs text-gray-400 hidden sm:inline">
                • آخر نشر: {formatRelativeAr(state?.publishedAt) || '—'}
              </span>
            )}

            <div className="flex-1" />

            <button
              onClick={openPreview}
              disabled={previewLoading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              معاينة
            </button>

            <button
              onClick={saveAllDirty}
              disabled={!anyDirty || saving}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-blue-50 border border-blue-100 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              تحديث الكل
            </button>

            <button
              onClick={revert}
              disabled={!hasUnpublished || reverting}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {reverting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              تجاهل التغييرات
            </button>

            <button
              onClick={onPublish}
              disabled={!hasUnpublished || publishing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              نشر
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* META DEFAULTS */}
          <SectionCard
            icon={FileText}
            title="العناوين والوصف الافتراضي"
            subtitle="القالب الذي يُستخدم على كل الصفحات ما لم يتم تخصيصه"
            dirty={dirtyMap.seoDefaults}
            saving={saving}
            accent="blue"
            onSave={() => saveSection('seoDefaults')}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="قالب العنوان (عربي)"
                  value={sections.seoDefaults.titleTemplate?.ar}
                  onChange={(v) => updateSection('seoDefaults', (s) => ({
                    ...s, titleTemplate: { ...(s.titleTemplate || {}), ar: v },
                  }))}
                  placeholder="%s | {{siteName}}"
                  hint="%s ← عنوان الصفحة، {{siteName}} ← اسم الموقع"
                />
                <TextField
                  label="Title Template (EN)"
                  dir="ltr"
                  value={sections.seoDefaults.titleTemplate?.en}
                  onChange={(v) => updateSection('seoDefaults', (s) => ({
                    ...s, titleTemplate: { ...(s.titleTemplate || {}), en: v },
                  }))}
                  placeholder="%s | {{siteName}}"
                />
              </div>

              {/* Live preview */}
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs">
                <p className="text-gray-500 mb-1">معاينة:</p>
                <p className="text-gray-800"><span className="text-gray-500">عربي:</span> {titlePreviewAr}</p>
                <p className="text-gray-800 mt-0.5" dir="ltr"><span className="text-gray-500">EN:</span> {titlePreviewEn}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="العنوان الافتراضي (عربي)"
                  value={sections.seoDefaults.defaultTitle?.ar}
                  onChange={(v) => updateSection('seoDefaults', (s) => ({
                    ...s, defaultTitle: { ...(s.defaultTitle || {}), ar: v },
                  }))}
                />
                <TextField
                  label="Default Title (EN)"
                  dir="ltr"
                  value={sections.seoDefaults.defaultTitle?.en}
                  onChange={(v) => updateSection('seoDefaults', (s) => ({
                    ...s, defaultTitle: { ...(s.defaultTitle || {}), en: v },
                  }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextArea
                  label="الوصف الافتراضي (عربي)"
                  value={sections.seoDefaults.defaultDescription?.ar}
                  onChange={(v) => updateSection('seoDefaults', (s) => ({
                    ...s, defaultDescription: { ...(s.defaultDescription || {}), ar: v },
                  }))}
                  rows={3}
                />
                <TextArea
                  label="Default Description (EN)"
                  dir="ltr"
                  value={sections.seoDefaults.defaultDescription?.en}
                  onChange={(v) => updateSection('seoDefaults', (s) => ({
                    ...s, defaultDescription: { ...(s.defaultDescription || {}), en: v },
                  }))}
                  rows={3}
                />
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">الكلمات المفتاحية</label>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addKeyword();
                      }
                    }}
                    placeholder="اكتب كلمة واضغط Enter"
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  />
                  <button
                    onClick={addKeyword}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                  >
                    <Plus className="w-4 h-4" /> إضافة
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(sections.seoDefaults.keywords || []).length === 0 ? (
                    <span className="text-xs text-gray-400">لا توجد كلمات مفتاحية بعد</span>
                  ) : (
                    (sections.seoDefaults.keywords || []).map((kw) => (
                      <span
                        key={kw}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium"
                      >
                        {kw}
                        <button
                          onClick={() => removeKeyword(kw)}
                          className="hover:text-blue-500"
                          title="إزالة"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* OG / TWITTER */}
          <SectionCard
            icon={Share2}
            title="OpenGraph و Twitter"
            subtitle="صورة المشاركة وحساب تويتر الرسمي"
            dirty={dirtyMap.seoDefaults}
            saving={saving}
            accent="pink"
            onSave={() => saveSection('seoDefaults')}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">صورة OG الافتراضية</label>
                {sections.seoDefaults.ogImageUrl ? (
                  <div className="space-y-2">
                    <div className="relative w-full rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                      {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                      <img
                        src={sections.seoDefaults.ogImageUrl}
                        alt="OG preview"
                        className="w-full h-56 object-cover"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setMediaPickerOpen(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        <ImageIcon className="w-4 h-4" />
                        تغيير
                      </button>
                      <button
                        onClick={() => updateSection('seoDefaults', (s) => ({ ...s, ogImageUrl: '' }))}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100"
                      >
                        <Trash2 className="w-4 h-4" />
                        إزالة
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 break-all" dir="ltr">
                      {sections.seoDefaults.ogImageUrl}
                    </p>
                  </div>
                ) : (
                  <button
                    onClick={() => setMediaPickerOpen(true)}
                    className="w-full py-8 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-blue-400 hover:bg-blue-50/40 transition-colors flex flex-col items-center justify-center"
                  >
                    <ImageIcon className="w-8 h-8 mb-2" />
                    <span className="text-sm font-medium">اختر صورة OG</span>
                    <span className="text-xs text-gray-400 mt-1">الأبعاد الموصى بها: 1200×630</span>
                  </button>
                )}
              </div>

              <TextField
                label="Twitter Handle"
                dir="ltr"
                value={sections.seoDefaults.twitterHandle}
                onChange={(v) => updateSection('seoDefaults', (s) => ({ ...s, twitterHandle: v }))}
                placeholder="@labtechs"
                hint="اسم المستخدم مع علامة @"
              />
            </div>
          </SectionCard>

          {/* SCHEMA.ORG */}
          <SectionCard
            icon={Building2}
            title="بيانات Schema.org"
            subtitle="معلومات مُهيكلة تساعد Google في فهم مختبرك"
            dirty={dirtyMap.schemaOrg}
            saving={saving}
            accent="blue"
            onSave={() => saveSection('schemaOrg')}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">نوع المنظمة</label>
                  <select
                    value={sections.schemaOrg.organizationType || 'MedicalBusiness'}
                    onChange={(e) => updateSection('schemaOrg', (s) => ({ ...s, organizationType: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white text-sm"
                  >
                    {ORG_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <TextField
                  label="تاريخ التأسيس"
                  type="date"
                  dir="ltr"
                  value={sections.schemaOrg.foundingDate}
                  onChange={(v) => updateSection('schemaOrg', (s) => ({ ...s, foundingDate: v }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TextField
                  label="نطاق الأسعار"
                  dir="ltr"
                  value={sections.schemaOrg.priceRange}
                  onChange={(v) => updateSection('schemaOrg', (s) => ({ ...s, priceRange: v }))}
                  placeholder="$$"
                  hint="$, $$, $$$"
                />
                <TextField
                  label="Latitude"
                  type="number"
                  dir="ltr"
                  value={sections.schemaOrg.geo?.lat}
                  onChange={(v) => updateSection('schemaOrg', (s) => ({
                    ...s, geo: { ...(s.geo || {}), lat: v },
                  }))}
                  placeholder="24.7136"
                />
                <TextField
                  label="Longitude"
                  type="number"
                  dir="ltr"
                  value={sections.schemaOrg.geo?.lng}
                  onChange={(v) => updateSection('schemaOrg', (s) => ({
                    ...s, geo: { ...(s.geo || {}), lng: v },
                  }))}
                  placeholder="46.6753"
                />
              </div>

              {/* Opening hours */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">ساعات العمل</label>
                  <button
                    onClick={addOpeningRow}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                  >
                    <Plus className="w-3.5 h-3.5" /> إضافة فترة
                  </button>
                </div>

                {(sections.schemaOrg.openingHoursSpecification || []).length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
                    لا توجد فترات عمل بعد — أضف أول فترة
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {(sections.schemaOrg.openingHoursSpecification || []).map((row, idx) => (
                      <li key={idx} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {WEEKDAYS.map((d) => {
                            const selected = (row.dayOfWeek || []).includes(d.value);
                            return (
                              <button
                                key={d.value}
                                onClick={() => {
                                  const current = new Set(row.dayOfWeek || []);
                                  if (selected) current.delete(d.value); else current.add(d.value);
                                  updateOpeningRow(idx, { ...row, dayOfWeek: Array.from(current) });
                                }}
                                className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                                  selected
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                {d.labelAr}
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500">من</span>
                            <input
                              type="time"
                              value={row.opens || ''}
                              onChange={(e) => updateOpeningRow(idx, { ...row, opens: e.target.value })}
                              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                              dir="ltr"
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-500">إلى</span>
                            <input
                              type="time"
                              value={row.closes || ''}
                              onChange={(e) => updateOpeningRow(idx, { ...row, closes: e.target.value })}
                              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                              dir="ltr"
                            />
                          </div>
                          <div className="flex-1" />
                          <button
                            onClick={() => removeOpeningRow(idx)}
                            className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50"
                            title="حذف الفترة"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </SectionCard>

          {/* SITEMAP DEFAULTS */}
          <SectionCard
            icon={Map}
            title="إعدادات sitemap الافتراضية"
            subtitle="القيم المستخدمة للصفحات غير المخصصة"
            dirty={dirtyMap.seoDefaults}
            saving={saving}
            accent="emerald"
            onSave={() => saveSection('seoDefaults')}
          >
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">الأولوية الافتراضية (priority)</label>
                  <span className="text-sm font-bold text-emerald-700">
                    {Number(sections.seoDefaults.sitemapDefaultPriority ?? 0.7).toFixed(1)}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={Number(sections.seoDefaults.sitemapDefaultPriority ?? 0.7)}
                  onChange={(e) => updateSection('seoDefaults', (s) => ({
                    ...s, sitemapDefaultPriority: Number(e.target.value),
                  }))}
                  className="w-full accent-emerald-600"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1" dir="ltr">
                  <span>0.0</span><span>0.5</span><span>1.0</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">التردد الافتراضي (changefreq)</label>
                <select
                  value={sections.seoDefaults.sitemapDefaultChangefreq || 'weekly'}
                  onChange={(e) => updateSection('seoDefaults', (s) => ({
                    ...s, sitemapDefaultChangefreq: e.target.value,
                  }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white text-sm"
                  dir="ltr"
                >
                  {CHANGEFREQ_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </SectionCard>

          {/* REDIRECTS */}
          <SectionCard
            icon={LinkIcon}
            title="إعادة التوجيه (Redirects)"
            subtitle="مفيدة بعد تغيير روابط الصفحات"
            dirty={dirtyMap.redirects}
            saving={saving}
            accent="amber"
            onSave={() => saveSection('redirects')}
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {(sections.redirects || []).length} قاعدة تحويل
                </span>
                <button
                  onClick={addRedirect}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                >
                  <Plus className="w-3.5 h-3.5" /> إضافة تحويل
                </button>
              </div>

              {(sections.redirects || []).length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
                  لا توجد تحويلات بعد — أضف أول قاعدة
                </div>
              ) : (
                <ul className="space-y-2">
                  {(sections.redirects || []).map((row, idx) => (
                    <li key={idx} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_40px] gap-2 items-start">
                        <input
                          value={row.from || ''}
                          onChange={(e) => updateRedirect(idx, { ...row, from: e.target.value })}
                          placeholder="/old-path"
                          dir="ltr"
                          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <input
                          value={row.to || ''}
                          onChange={(e) => updateRedirect(idx, { ...row, to: e.target.value })}
                          placeholder="/new-path"
                          dir="ltr"
                          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <select
                          value={row.statusCode || 301}
                          onChange={(e) => updateRedirect(idx, { ...row, statusCode: Number(e.target.value) })}
                          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          dir="ltr"
                        >
                          {REDIRECT_CODES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => removeRedirect(idx)}
                          className="p-2 rounded-lg text-blue-500 hover:bg-blue-50 justify-self-start"
                          title="حذف"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {redirectErrors[idx] ? (
                        <p className="text-xs text-blue-600 mt-2">{redirectErrors[idx]}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </SectionCard>

          {/* ROBOTS EXTRA */}
          <SectionCard
            icon={Bot}
            title="Robots.txt — سطور إضافية"
            subtitle="تُضاف لنهاية ملف robots.txt المولّد تلقائياً"
            dirty={dirtyMap.robotsExtra}
            saving={saving}
            accent="slate"
            onSave={() => saveSection('robotsExtra')}
          >
            <div className="space-y-3">
              <textarea
                value={sections.robotsExtra || ''}
                onChange={(e) => updateSection('robotsExtra', e.target.value)}
                rows={6}
                dir="ltr"
                placeholder={'User-agent: *\nDisallow: /private'}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-mono"
              />
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-xs text-slate-600 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  هذه السطور تُلحق بنهاية robots.txt المُنشأ تلقائياً.
                  لا تضف قواعد تمنع Googlebot من الوصول للصفحات المهمة.
                </span>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Media Picker */}
      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onPick={onOgImagePick}
        folder="sections"
      />
    </div>
  );
};

export default SEOSettings;
